import React, { useContext } from "react";
import { Card, Grid } from "@mui/material";
import classNames from "classnames";
import { useMutation } from "@apollo/client";
import { Button } from "components/haven-design";
import { SendEmailVerification, SendEmailVerificationVariables } from "types/gql-types";
import { Page } from "../type";
import { SEND_EMAIL_VERIFICATION } from "../query";
import LoginModalContext from "../context/modal";
import { LoginCardTitle, LoginCardSubtitle } from "./shared-components";
import styles from "./Login.module.css";
import { loginAuthColumnSx } from "./styles";

type EmailProvider = "gmail" | "yahoo" | "outlook" | "other";

const getProviderFromEmail = (email: string): EmailProvider => {
  const domain = (email.split("@")[1] || "").toLowerCase();
  if (["gmail.com", "googlemail.com"].includes(domain)) return "gmail";
  if (["yahoo.com", "ymail.com", "rocketmail.com"].includes(domain)) return "yahoo";
  if (["outlook.com", "hotmail.com", "live.com", "msn.com"].includes(domain)) return "outlook";
  return "other";
};

const getProviderUrl = (provider: EmailProvider, email: string): string => {
  const encoded = encodeURIComponent(email);
  switch (provider) {
    case "gmail":
      return `https://accounts.google.com/signin/v2/identifier?service=mail&identifier=${encoded}`;
    case "yahoo":
      return `https://login.yahoo.com/?username=${encoded}`;
    case "outlook":
      return `https://login.live.com/login.srf?login_hint=${encoded}`;
    default:
      return "";
  }
};

const providerOptions: Array<{ key: "gmail" | "yahoo" | "outlook"; label: string }> = [
  { key: "gmail", label: "Gmail" },
  { key: "outlook", label: "Outlook" },
  { key: "yahoo", label: "" },
];

const iconWrapperStyle: React.CSSProperties = {
  display: "inline-flex",
  marginRight: 8,
  verticalAlign: "middle",
};

const ProviderIcon: React.FC<{ name: EmailProvider }> = ({ name }) => (
  <span style={iconWrapperStyle} aria-hidden="true">
    <img src={`/images/${name}.png`} alt="" width={name === "yahoo" ? 60 : 18} height={18} />
  </span>
);

// (No other changes outside ProviderIcon)

const getVerifyRedirectURL = (inModal: boolean, formId?: string, username?: string) => {
  const usernameParam = username ? `u=${encodeURIComponent(username)}` : null;
  let link = `${window.location.href}${window.location.href.includes("?") ? "&" : "?"}verify=true`;

  if (formId) link = `${window.location.origin}/form/${formId}`;
  else if (!inModal) link = `${window.location.origin}/email-verified`;

  const joiner = link.includes("?") ? "&" : "?";
  return `${link}${joiner}${usernameParam}`;
};

// eslint-disable-next-line max-lines-per-function
const WaitToVerify: Page<"wait-to-verify"> = (props) => {
  const { inModal } = useContext(LoginModalContext);
  const [sendEmailVerification] =
    useMutation<SendEmailVerification, SendEmailVerificationVariables>(SEND_EMAIL_VERIFICATION);

  React.useEffect(() => {
    const redirectURL = getVerifyRedirectURL(inModal, props.formId, props.username);
    sendEmailVerification({ variables: { email: props.username, redirectUrl: redirectURL } });
  }, [props.username, inModal, props.formId]);

  // No internal navigation needed here; show quick links to common email providers.

  return (
    <div className={styles.form}>
      <Card className={classNames(styles.loginCard, { [styles.borderlessCard]: inModal })}>
        <Grid
          container
          spacing={2}
          direction="column"
          alignItems="center"
          justifyContent="center"
        >
          <Grid>
            <LoginCardTitle title="Verify your account" />
          </Grid>
          <Grid>
            <LoginCardSubtitle>
              <strong>
                We sent an email to {props.username}
              </strong> containing a link to securely verify
              your account. Once clicked, you&apos;ll be able to login with your new user account.
            </LoginCardSubtitle>
          </Grid>
          {(() => {
            const provider = getProviderFromEmail(props.username);
            if (provider === "other") {
              return (
                <Grid container spacing={2} justifyContent="center">
                  {providerOptions.map(({ key, label }) => (
                    <Grid key={key} size={{ xs: 12 }} sx={loginAuthColumnSx}>
                      <Button
                        size="L"
                        color="secondary"
                        fullWidth
                        className={styles.loginButton}
                        onClick={() => window.open(getProviderUrl(key, props.username), "_blank", "noopener")}
                      >
                        {key && <ProviderIcon name={key} />}
                        {label}
                      </Button>
                    </Grid>
                  ))}
                </Grid>
              );
            }

            const url = getProviderUrl(provider, props.username);
            const selected = providerOptions.find((option) => option.key === provider);
            const label = selected ? selected.label : "Mail";
            return (
              <Grid size={{ xs: 12 }} sx={loginAuthColumnSx}>
                <Button
                  size="L"
                  color="secondary"
                  fullWidth
                  className={styles.loginButton}
                  onClick={() => window.open(url, "_blank", "noopener")}
                >
                  {selected?.key && <ProviderIcon name={selected.key} />}
                  {label}
                </Button>
              </Grid>
            );
          })()}
        </Grid>
      </Card>
    </div>
  );
};

export default WaitToVerify;
